/*
 * EVENT BUS CONSTANTS
 * 
 * Centralized event bus definitions for the RLP CRM application.
 * This file contains internal Event Bus events used for component communication.
 * 
 * Simplified Structure:
 * - Flat hierarchy with direct access to all events via EVENTS object
 * - Chat events: Opening chat interface with prompts
 * 
 * Usage:
 * - Import EVENTS for event names (simplified from EVENT_BUS)
 * - Use TypeScript interfaces for type safety
 * - Reference this file when adding new event bus events
 *
 * Recent changes:
 * - Restored validated chat draft handoffs through the maintained note/task modal events.
 * - Added a route-preserving event for opening the shared task composer.
 * - Added optional Account/Contact context to prepopulate the shared task composer.
 */

// =============================================================================
// SIMPLIFIED EVENT BUS CONSTANTS
// =============================================================================

export const EVENTS = {

  // UI events
  CHAT_OPEN: 'chat:open',
  CHAT_SESSION_UPDATED: 'chat:session-updated',
  CHAT_PROGRESS_CLEARED: 'chat:progress-cleared',
  OPEN_NOTE_MODAL: 'ui:open_note_modal',
  OPEN_TASK_MODAL: 'ui:open_task_modal',
  NOTES_UPDATED: 'notes:updated',
  PROMPTS_REFRESH: 'prompts:refresh',

  // Drag and drop events
  NOTE_DRAG_TO_CHAT: 'note:drag-to-chat',
  CHAT_OPEN_FOR_DROP: 'chat:open-for-drop',

} as const;

// TypeScript interfaces for Event Bus event data
export interface ChatOpenEventData {
  prompt?: string;
  createNewSession?: boolean;
}

export interface ChatSessionUpdatedEventData {
  sessionId: number;
  title?: string;
  reason?: string;
}

export interface OpenNoteModalEventData {
  contactId?: number;
  accountId?: number;
  teamId?: number;
  initialContent?: string;
}

export interface OpenTaskModalEventData {
  accountId?: number;
  accountName?: string;
  contactId?: number;
  contactName?: string;
  initialTitle?: string;
  initialDescription?: string;
  initialDueDate?: string;
  initialPriority?: 'Low' | 'Medium' | 'High';
}

export interface NotesUpdatedEventData {
  noteId: number;
  accountId?: number;
  contactId?: number;
}

/** Internal card drag payloads carry one record reference; Chat retrieves permitted evidence. */
export type CardDragToChatEventData =
  | { noteId: number }
  | { accountId: number }
  | { contactId: number };

export interface ChatOpenForDropEventData {
  // No additional data needed for this event
}

// =============================================================================
// TYPE GUARDS AND UTILITIES
// =============================================================================

// Type guard functions for runtime validation
export const isEventBusEvent = (eventType: string): boolean => {
  const allEvents = Object.values(EVENTS);
  return allEvents.includes(eventType as any);
};

// =============================================================================
// EVENT BUS TYPE EXPORTS
// =============================================================================

// Export event types for easier imports
export type EventType = typeof EVENTS[keyof typeof EVENTS];
